import { GenericServices } from "./generic.services";
import { CONFIGURATION } from "../common/uri/configuration-uri.constants";
import { ApiResponse } from "../models/app-configuration";
import { commonUri } from "../common";
import { IAiDescriptionSettings } from "../models/configuration/ai-description-settings";

/** One JSON-patch operation against the system-config document. */
export interface ISystemConfigPatch {
    op: string;
    path: string;
    value: string | number | boolean | null;
}

export class ConfigurationServices extends GenericServices {
    getSystemConfigs(params) {
        return this.executeSelecting(params, `${CONFIGURATION.getSystemConfigs}/info`);
    }

    postSystemConfigs(originalParams, newParams) {
        return this.executeUpdatingCompare(originalParams, newParams, `${CONFIGURATION.getSystemConfigs}`);
    }

    /**
     * Resolves with the API's answer, or undefined on a transport failure; a refused PATCH comes
     * back with `isSuccess: false` instead of rejecting. Treat anything without `isSuccess` as not saved.
     */
    updateSystemConfigs(param: ISystemConfigPatch[]): Promise<ApiResponse | undefined> {
        return super.executePatch<ISystemConfigPatch[], ApiResponse>(param, CONFIGURATION.getSystemConfigs)
            .then(response => response?.data);
    }

    getGoogleAnalyticsList() {
        return this.executeSelecting({}, `${CONFIGURATION.getListGoogleAnalytics}`).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    getInternetConfiguration(mediaOnlineId: number, siteId: number) {
        const url = CONFIGURATION.getInternetConfiguration
            .replace("{mediaOnlineId}", mediaOnlineId.toString())
            .replace("{siteId}", siteId.toString());
        return this.executeSelecting({}, url).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    getConfigValue(configKeys: string[]){
        let url = CONFIGURATION.getValueConfigs;
        url += configKeys.toString();
        return this.executeSelecting({}, url).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    /**
     * Agency extra instruction per language (nl, fr, en, de)
     * appended to the AI description prompts of that language. Any
     * authenticated user may read it because the generator needs the
     * value; the returned CanEdit is the General configuration right
     * (right 9) and only gates the editor (omnicasa-settings).
     */
    getAiDescriptionSettings(): Promise<IAiDescriptionSettings> {
        return this.executeSelecting({}, CONFIGURATION.getAiDescriptionSettings).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    async addGoogleAnalytic(payload) {
        try {
            return await this.executeSelectingPost(payload, `${CONFIGURATION.inseGoogleAnalytic}`);
        } catch (error) {
            return error;
        }
    }
    async updateGoogleAnalytic(payload) {
        try {
            return await this.executeSelectingPost(payload, `${CONFIGURATION.updateGoogleAnalytic}`);
        } catch (error) {
            return error;
        }
    }
    async deleteGoogleAnalytic(id) {
        try {
            const url = CONFIGURATION.deleteGoogleAnalytic.replace("{id}", id);
            return await this.executeDeleting(url);
        } catch (error) {
            return error;
        }
    }

    getSitesData() {
        return this.executeSelecting({}, `${commonUri.getSites}`).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    getAcaAuthorization() {
        return this.executeSelecting({}, `${commonUri.getAcaAuthorization}`).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    refreshAcaToken() {
        return this.executeSelecting({}, `${commonUri.refreshAcaToken}`).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    getAcaArticleTypes() {
        return this.executeSelecting({}, `${commonUri.getAcaArticleTypes}`).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    getAcaInspectionTypes(articleSubTypeId: number) {
        const url = commonUri.getAcaInspectionTypes.replace(":articleSubTypeId", articleSubTypeId.toString());
        return this.executeSelecting({}, url).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }

    getAcaInstallSubTypes() {
        return this.executeSelecting({}, `${commonUri.getAcaInstallSubTypes}`).then((res: ApiResponse) => {
            return res.isSuccess ? Promise.resolve(res.data) : Promise.reject(res.message);
        }).catch(err => {
            return Promise.reject(err);
        })
    }
}